import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import CancelIcon       from '@mui/icons-material/Cancel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SpeedOutlinedIcon           from '@mui/icons-material/SpeedOutlined';
import CurrencyRupeeOutlinedIcon   from '@mui/icons-material/CurrencyRupeeOutlined';
import SupportAgentOutlinedIcon    from '@mui/icons-material/SupportAgentOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { motion } from 'framer-motion';
import SectionHeader from './SectionHeader';
import { C, F, SH } from '../styles/tokens';
import { btnBlueSx } from '../styles/mixins';

// ── Data ───────────────────────────────────────────────────────────────────────

const USP_HIGHLIGHTS = [
  { icon: <SpeedOutlinedIcon />,            label: 'Fast Turnaround',     desc: 'Registered in 3–7 days' },
  { icon: <CurrencyRupeeOutlinedIcon />,    label: 'Fixed Pricing',       desc: 'No hidden charges, ever' },
  { icon: <SupportAgentOutlinedIcon />,     label: 'WhatsApp Support',    desc: 'Real humans, quick replies' },
  { icon: <WorkspacePremiumOutlinedIcon />, label: 'Expert Team',         desc: 'CAs, CSs & legal advisors' },
];

const COMPARISON = [
  { feature: 'Qualified CA & CS Professionals',   pracco: true,  others: true  },
  { feature: 'Transparent, Fixed Pricing',         pracco: true,  others: false },
  { feature: 'Reg + Tax + GST Under One Roof',     pracco: true,  others: false },
  { feature: 'Dedicated WhatsApp Support',         pracco: true,  others: false },
  { feature: 'Fast Turnaround (3–7 Days)',          pracco: true,  others: false },
  { feature: 'Zero Hidden Charges',                pracco: true,  others: false },
  { feature: 'Post-Registration Compliance Help',  pracco: true,  others: false },
  { feature: 'Dedicated Account Manager',          pracco: true,  others: false },
  { feature: 'Affordable for Startups & SMEs',     pracco: true,  others: false },
  { feature: 'Real-Time Status Updates',           pracco: true,  others: false },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

const Tick = ({ yes }) => (
  yes
    ? <CheckCircleIcon sx={{ fontSize: 20, color: C.green }} />
    : <CancelIcon      sx={{ fontSize: 20, color: '#FCA5A5' }} />
);

// ── Main component ─────────────────────────────────────────────────────────────

const Partner = () => (
  <Box sx={{ backgroundColor: C.bg, px: { xs: 2, md: 6 }, py: { xs: 7, md: 10 } }}>
    <SectionHeader
      label="Why Choose Us"
      title="Pracco vs The Rest"
      subtitle="We don't just file documents — we become your compliance partner from day one."
    />

    {/* USP highlight chips */}
    <Box
      sx={{
        display:        'flex',
        flexWrap:       'wrap',
        justifyContent: 'center',
        gap:            2,
        mb:             { xs: 5, md: 7 },
      }}
    >
      {USP_HIGHLIGHTS.map((u, idx) => (
        <motion.div
          key={u.label}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: idx * 0.08 }}
        >
          <Box
            sx={{
              display:         'flex',
              alignItems:      'center',
              gap:             1.25,
              backgroundColor: C.white,
              border:          `1px solid ${C.border}`,
              borderRadius:    '12px',
              px:              2.5,
              py:              1.25,
              boxShadow:       SH.card,
            }}
          >
            <Box sx={{ color: C.blue }}>{u.icon}</Box>
            <Box>
              <Typography sx={{ fontFamily: F.heading, fontWeight: 700, fontSize: 13.5, color: C.navy, lineHeight: 1.2 }}>
                {u.label}
              </Typography>
              <Typography sx={{ fontFamily: F.body, fontSize: 11.5, color: C.textMuted }}>
                {u.desc}
              </Typography>
            </Box>
          </Box>
        </motion.div>
      ))}
    </Box>

    {/* Comparison table */}
    <Box sx={{ maxWidth: 840, mx: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <Box
          sx={{
            backgroundColor: C.white,
            border:          `1px solid ${C.border}`,
            borderRadius:    '20px',
            overflow:        'hidden',
            boxShadow:       SH.cardHover,
          }}
        >
          {/* ── Column headers ── */}
          <Box
            sx={{
              display:             'grid',
              gridTemplateColumns: { xs: '1fr 88px 104px', md: '1fr 150px 168px' },
              borderBottom:        `1px solid ${C.border}`,
            }}
          >
            {/* Feature column header */}
            <Box
              sx={{
                px:          { xs: 2, md: 3 },
                py:          2.5,
                borderRight: `1px solid ${C.border}`,
              }}
            >
              <Typography
                sx={{
                  fontFamily:    F.heading,
                  fontWeight:    700,
                  fontSize:      { xs: 11, md: 12 },
                  color:         C.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                }}
              >
                Feature
              </Typography>
            </Box>

            {/* Others header */}
            <Box
              sx={{
                px:              { xs: 1.5, md: 2 },
                py:              2.5,
                textAlign:       'center',
                backgroundColor: '#F8FAFC',
                borderRight:     `1px solid ${C.border}`,
              }}
            >
              <Typography sx={{ fontFamily: F.heading, fontWeight: 700, fontSize: { xs: 13, md: 15 }, color: C.textMid }}>
                Others
              </Typography>
              <Typography sx={{ fontFamily: F.body, fontSize: { xs: 10, md: 11.5 }, color: C.textLight, mt: 0.3 }}>
                Typical firms
              </Typography>
            </Box>

            {/* Pracco header */}
            <Box
              sx={{
                px:        { xs: 1.5, md: 2 },
                py:        2.5,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
              }}
            >
              <Typography sx={{ fontFamily: F.heading, fontWeight: 700, fontSize: { xs: 13, md: 15 }, color: C.white }}>
                Pracco
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                <Box
                  sx={{
                    backgroundColor: C.amber,
                    color:           C.white,
                    fontFamily:      F.heading,
                    fontWeight:      700,
                    fontSize:        { xs: 8.5, md: 9.5 },
                    letterSpacing:   0.5,
                    px:              1,
                    py:              0.3,
                    borderRadius:    '20px',
                  }}
                >
                  RECOMMENDED
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── Feature rows ── */}
          {COMPARISON.map((row, idx) => (
            <Box
              key={row.feature}
              sx={{
                display:             'grid',
                gridTemplateColumns: { xs: '1fr 88px 104px', md: '1fr 150px 168px' },
                backgroundColor:     idx % 2 === 0 ? C.white : '#F8FAFC',
                '&:last-child > div': { borderBottom: 'none' },
              }}
            >
              <Box
                sx={{
                  px:          { xs: 2, md: 3 },
                  py:          { xs: 1.3, md: 1.5 },
                  display:     'flex',
                  alignItems:  'center',
                  borderBottom: `1px solid ${C.borderLight}`,
                  borderRight:  `1px solid ${C.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: F.body,
                    fontWeight: 500,
                    fontSize:   { xs: 12.5, md: 14 },
                    color:      C.text,
                    lineHeight: 1.4,
                  }}
                >
                  {row.feature}
                </Typography>
              </Box>

              <Box
                sx={{
                  display:         'flex',
                  alignItems:      'center',
                  justifyContent:  'center',
                  borderBottom:    `1px solid ${C.borderLight}`,
                  borderRight:     `1px solid ${C.border}`,
                  backgroundColor: idx % 2 === 0 ? C.white : '#F1F5F9',
                }}
              >
                <Tick yes={row.others} />
              </Box>

              <Box
                sx={{
                  display:         'flex',
                  alignItems:      'center',
                  justifyContent:  'center',
                  borderBottom:    `1px solid rgba(29,78,216,0.08)`,
                  backgroundColor: idx % 2 === 0 ? '#EFF6FF' : '#E8F0FE',
                }}
              >
                <Tick yes={row.pracco} />
              </Box>
            </Box>
          ))}
        </Box>
      </motion.div>
    </Box>

    {/* CTA */}
    <Box sx={{ textAlign: 'center', mt: 6 }}>
      <Typography sx={{ fontFamily: F.body, fontSize: 15, color: C.textMid, mb: 3 }}>
        Join 10,000+ businesses that chose Pracco for a smoother start.
      </Typography>
      <Button
        component="a"
        href="https://wa.me/9311817126"
        target="_blank"
        rel="noopener noreferrer"
        variant="contained"
        size="large"
        endIcon={<ArrowForwardIcon />}
        sx={{ ...btnBlueSx, fontSize: 15, px: 5, py: 1.6 }}
      >
        Get Started with Pracco
      </Button>
    </Box>
  </Box>
);

export default Partner;
